import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type { UserProfile, UserProfileRole, DerivedTenant } from '../types/models/UserProfile';
import axiosInstance from '../api/axiosInstance';
import { useAuthStore } from './authStore';

const TENANT_STORAGE_KEY = 'helios-current-tenant';

// Deduplication: tracks in-flight fetchProfile to prevent concurrent duplicate calls
// (e.g. React StrictMode double-firing effects, multiple mount points)
let _fetchProfilePromise: Promise<void> | null = null;

// Helper to safely check if we're in development mode
const isDevelopment = (): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - process is defined by webpack DefinePlugin at build time
    return process.env.NODE_ENV !== 'production';
  } catch {
    return true;
  }
};

const deriveTenants = (roles: UserProfileRole[]): DerivedTenant[] => {
  const tenantMap = new Map<string, string>();
  roles.forEach((r) => {
    if (r.tenantId && r.tenantName) {
      tenantMap.set(r.tenantId, r.tenantName);
    }
  });
  return Array.from(tenantMap.entries()).map(([tenantId, tenantName]) => ({
    tenantId,
    tenantName,
  }));
};

export interface UserProfileState {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  currentTenantId: string | null;

  fetchProfile: () => Promise<void>;
  setCurrentTenant: (tenantId: string) => void;
  derivedTenants: () => DerivedTenant[];
  currentTenantRoles: () => UserProfileRole[];
  reset: () => void;
}

export const useUserProfileStore = create<UserProfileState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      profile: null,
      isLoading: true,
      error: null,
      currentTenantId: null,

      fetchProfile: async () => {
        // Deduplicate: if a fetch is already in-flight, return the existing promise
        if (_fetchProfilePromise) return _fetchProfilePromise;

        set({ isLoading: true, error: null }, false, 'fetchProfile/start');

        _fetchProfilePromise = (async () => {
          try {
            const response = await axiosInstance.get<UserProfile>('/api/v1/me');
            const profile = response.data;

            const derived = deriveTenants(profile.roles);

            // Resolve currentTenantId
            const stored = localStorage.getItem(TENANT_STORAGE_KEY);
            const storedIsValid = stored && derived.some((t) => t.tenantId === stored);

            let currentTenantId: string;
            if (storedIsValid) {
              currentTenantId = stored;
            } else {
              // For users whose home tenant has no roles (e.g. internal/workforce users
              // assigned only as Viewer in a client project), fall back to the first
              // derived tenant so usePermissions filter can match their actual roles.
              const homeInDerived = derived.some((t) => t.tenantId === profile.user.tenantId);
              currentTenantId =
                homeInDerived || derived.length === 0 ? profile.user.tenantId : derived[0].tenantId;
              // Clear stale localStorage entry
              if (stored) {
                localStorage.removeItem(TENANT_STORAGE_KEY);
              }
            }

            set(
              { profile, isLoading: false, error: null, currentTenantId },
              false,
              'fetchProfile/success'
            );
          } catch (error: unknown) {
            const axiosError = error as { response?: { status?: number }; message?: string };

            if (axiosError.response?.status === 401) {
              // Redirect to login via authStore
              useAuthStore.getState().logout();
              set({ isLoading: false, error: null }, false, 'fetchProfile/401');
              return;
            }

            const message = axiosError.message || 'Failed to load user profile';
            set({ isLoading: false, error: message }, false, 'fetchProfile/error');
          } finally {
            _fetchProfilePromise = null;
          }
        })();

        return _fetchProfilePromise;
      },

      setCurrentTenant: (tenantId: string) => {
        localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
        set({ currentTenantId: tenantId }, false, 'setCurrentTenant');
      },

      derivedTenants: () => {
        const { profile } = get();
        if (!profile) return [];
        return deriveTenants(profile.roles);
      },

      currentTenantRoles: () => {
        const { profile, currentTenantId } = get();
        if (!profile || !currentTenantId) return [];
        return profile.roles.filter(
          (r) => r.scope === 'Platform' || r.tenantId === currentTenantId
        );
      },

      reset: () => {
        set({ profile: null, isLoading: true, error: null, currentTenantId: null }, false, 'reset');
      },
    })),
    {
      name: 'Helios UserProfile Store',
      enabled: isDevelopment(),
    }
  )
);
